import type { ReactNode } from 'react';
import styled from 'styled-components';
import { animated, useTransition } from '@react-spring/web';
import { SizeSpacing16, SizeSpacing32 } from 'autogen/design-tokens/global/tokens';
import { Button } from 'components/Button';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import { useDrawerStore } from '../hooks/useDrawer';
import type { DrawerActions } from '../shared/types';

export type HeaderRendererProps = DrawerActions;

const HeaderContainer = styled.div`
    position: relative;
    display: flex;
    gap: ${({ theme }) => theme.Spacing8};
    align-items: center; flex: 0 0 auto;
    padding: ${SizeSpacing32} ${SizeSpacing16};
`;

const BackbuttonContainer = styled(Flex.Column)`
    min-width: 40px;
`;

const AnimatedButton = animated<any>(Button.WithIcon);

const DefaultBackButton = (props) => {
    const drawerInstances = useDrawerStore((state) => state.drawerInstances);
    const hasMultipleInstancesOpen = drawerInstances.length > 1;
    const hasGoBack = hasMultipleInstancesOpen;

    const transition = useTransition(hasGoBack, {
        from: { x: '50%', opacity: 0 },
        enter: { x: '0%', opacity: 1 },
        leave: { x: '50%', opacity: 0 }
    });

    return transition(
        (styles, active) =>
            active && (
                <AnimatedButton
                    variant="navigation"
                    icon={<Icon type="ArrowLeft" size={16} />}
                    style={styles}
                    {...props}
                />
            )
    );
};

const DefaultHeader = ({ closeAll, closeCurrent, children }: HeaderRendererProps & { children?: ReactNode }) => {
    return (
        <HeaderContainer>
            <BackbuttonContainer alignItems="start" grow={0}>
                <DefaultBackButton onClick={closeCurrent} />
            </BackbuttonContainer>

            <Flex.Column alignItems="center" grow={1}>
                {children}
            </Flex.Column>

            <Flex.Column alignItems="end" grow={0}>
                <Button.WithIcon variant="navigation" icon={<Icon type="Close" size={16} />} onClick={closeAll} />
            </Flex.Column>
        </HeaderContainer>
    );
};

type WithHeadingProps = {
    heading?: string;
};

const DefaultHeaderWithHeading = ({ heading, ...restProps }: HeaderRendererProps & WithHeadingProps) => {
    return (
        <DefaultHeader {...restProps}>
            <Typography.Headline size="L">{heading}</Typography.Headline>
        </DefaultHeader>
    );
};

const DefaultHeaderRenderer = Object.assign(DefaultHeader, {
    WithHeading: DefaultHeaderWithHeading
});

export default DefaultHeaderRenderer;
